/**
 * MeasureItemCard - 계측 항목 카드 (모바일)
 *
 * 계측 데이터를 읽기 모드로 표시하는 카드 컴포넌트
 * - measureFormConfig 기반 타입별 필드 동적 렌더링
 * - 각 필드 개별 클릭 시 FieldEditModal로 해당 필드만 편집
 * - 판정 결과: JUDGMENT_DISPLAY 상수 사용
 * - 평가(discussion) 영역 인풋박스 디자인
 * - 삭제 버튼 빨간색 배경
 */

import { useState, useCallback } from "react";
import {
  MeasureType,
  JudgmentResultEnum,
  JUDGMENT_DISPLAY,
  getMeasureFormConfig,
  MeasureTypeNames,
} from "@ict/shared";
import type { FieldConfig } from "@ict/shared";
import { Trash2, Ruler } from "lucide-react";
import { MeasurePhoto } from "./MeasurePhoto";
import { FieldEditModal } from "./FieldEditModal";
import { useMeasurePhoto } from "@/hooks/useMeasurePhoto";

/** 판정 결과별 색상 스타일 */
const JUDGMENT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  [JudgmentResultEnum.GOOD]: {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
  },
  [JudgmentResultEnum.CAUTION]: {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    border: "border-yellow-200",
  },
  [JudgmentResultEnum.BAD]: {
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
  },
  [JudgmentResultEnum.NA]: {
    bg: "bg-gray-50",
    text: "text-gray-500",
    border: "border-gray-200",
  },
};

export interface MeasureItemCardProps {
  measureType: MeasureType;
  data: Record<string, unknown>;
  index: number;
  /** 카드 헤더에 #N 형식으로 표시할 번호 (없으면 typeName 표시) */
  displayNumber?: number;
  onDelete: (type: MeasureType, index: number) => void;
  /** mutation 로딩 상태 (편집 모달에서 사용) */
  mutationLoading?: boolean;
  // Photo 연동
  projectId: string;
  docKey: string;
  machineId: string;
  updateMeasure: (
    type: MeasureType,
    index: number,
    data: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>;
  /** 로컬 캐시만 업데이트 (syncQueue 추가 없음) - 오프라인 사진 이중 큐 방지 */
  updateMeasureLocalOnly?: (
    type: MeasureType,
    index: number,
    data: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>;
}

/**
 * 판정 배지 렌더링
 */
function JudgmentBadge({ result }: { result?: string | null }) {
  if (!result) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-400">
        미판정
      </span>
    );
  }

  const display = JUDGMENT_DISPLAY[result as keyof typeof JUDGMENT_DISPLAY];
  const colors = JUDGMENT_COLORS[result] ?? JUDGMENT_COLORS[JudgmentResultEnum.NA];

  if (!display) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${colors.bg} ${colors.text} ${colors.border}`}
    >
      <span>{display.symbol}</span>
      <span>{display.label}</span>
    </span>
  );
}

/** 판정 버튼 순서 정의 */
const JUDGMENT_BUTTONS = [
  JudgmentResultEnum.GOOD,
  JudgmentResultEnum.CAUTION,
  JudgmentResultEnum.BAD,
  JudgmentResultEnum.NA,
] as const;

/** 판정 토글 버튼 선택 상태 색상 (배경, 텍스트) */
const JUDGMENT_SELECTED_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  [JudgmentResultEnum.GOOD]: {
    bg: "bg-green-500",
    text: "text-white",
    border: "border-green-500",
  },
  [JudgmentResultEnum.CAUTION]: {
    bg: "bg-yellow-500",
    text: "text-white",
    border: "border-yellow-500",
  },
  [JudgmentResultEnum.BAD]: {
    bg: "bg-red-500",
    text: "text-white",
    border: "border-red-500",
  },
  [JudgmentResultEnum.NA]: {
    bg: "bg-gray-400",
    text: "text-white",
    border: "border-gray-400",
  },
};

/**
 * 판정 토글 버튼 그룹
 * - 4개 버튼 가로 균등 배분
 * - 선택/미선택 상태 시각적 구분
 * - 같은 버튼 재클릭 시 선택 해제
 */
function JudgmentToggleButtons({
  result,
  onToggle,
}: {
  result?: string | null;
  onToggle: (value: JudgmentResultEnum | null) => void;
}) {
  return (
    <div className="flex gap-2">
      {JUDGMENT_BUTTONS.map((value) => {
        const display = JUDGMENT_DISPLAY[value];
        const isSelected = result === value;
        const selectedStyles = JUDGMENT_SELECTED_STYLES[value];

        return (
          <button
            key={value}
            type="button"
            className={`flex-1 h-9 flex items-center justify-center gap-1 rounded-lg border text-[13px] font-medium transition-colors ${
              isSelected
                ? `${selectedStyles.bg} ${selectedStyles.text} ${selectedStyles.border}`
                : "bg-white text-gray-500 border-gray-200 active:bg-gray-50"
            }`}
            onClick={() => onToggle(isSelected ? null : value)}
            aria-pressed={isSelected}
            aria-label={`판정: ${display.label}`}
          >
            <span>{display.symbol}</span>
            <span>{display.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** 편집 중인 필드 상태 타입 */
interface EditingFieldState {
  field: FieldConfig;
  value: unknown;
  /** text 필드를 textarea로 렌더링할지 여부 */
  inputType?: "input" | "textarea";
}

/**
 * 읽기 모드 필드값 표시 (개별 클릭 가능)
 */
function ReadOnlyField({
  field,
  value,
  onClick,
}: {
  field: FieldConfig;
  value: unknown;
  onClick: () => void;
}) {
  const displayValue = (() => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    // select 타입: option label 찾기
    if (field.type === "select" && field.options) {
      const option = field.options.find((o) => o.value === String(value));
      return option?.label ?? String(value);
    }
    return String(value);
  })();

  const isEmpty = displayValue === "-";

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between py-2 active:bg-gray-50 transition-colors"
    >
      <span className="text-[13px] text-gray-500 flex-shrink-0">
        {field.label}
        {field.required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      <span
        className={`rounded-lg border px-3 py-1.5 text-[13px] text-right min-w-[120px] ${
          isEmpty
            ? "border-gray-200 bg-gray-50 text-gray-400"
            : "border-gray-200 bg-gray-50 text-gray-800 font-medium"
        }`}
      >
        {isEmpty ? "입력" : displayValue}
        {field.unit && <span className="text-gray-400 ml-0.5">{field.unit}</span>}
      </span>
    </button>
  );
}

export function MeasureItemCard({
  measureType,
  data,
  index,
  displayNumber,
  onDelete,
  mutationLoading,
  projectId,
  docKey,
  machineId,
  updateMeasure,
  updateMeasureLocalOnly,
}: MeasureItemCardProps) {
  // 필드별 개별 편집 상태
  const [editingField, setEditingField] = useState<EditingFieldState | null>(null);

  const { uploadPhoto, deletePhoto, uploadingSlot } = useMeasurePhoto({
    projectId,
    docKey,
    machineId,
    measureType,
    measureIndex: index,
    measureData: data,
    updateMeasure,
    updateMeasureLocalOnly,
  });

  const typeName = MeasureTypeNames[measureType as keyof typeof MeasureTypeNames] ?? measureType;
  const config = getMeasureFormConfig(measureType);
  const result = data.result as string | undefined;
  const discussion = data.discussion as string | undefined;
  const photo = data.photo as { checkUrl?: string; resultUrl?: string } | undefined;

  const handleDelete = useCallback(() => {
    onDelete(measureType, index);
  }, [onDelete, measureType, index]);

  /** 단일 필드 편집 저장 콜백 */
  const handleFieldSave = useCallback(
    async (newValue: unknown) => {
      if (!editingField) return;
      const result = await updateMeasure(measureType, index, {
        ...data,
        [editingField.field.name]: newValue,
      });
      if (result.success) {
        setEditingField(null);
      }
    },
    [updateMeasure, measureType, index, data, editingField]
  );

  /** 판정 토글 콜백 - 서버에 즉시 저장 */
  const handleJudgmentToggle = useCallback(
    (newResult: JudgmentResultEnum | null) => {
      const isManual = newResult !== null;
      updateMeasure(measureType, index, {
        ...data,
        result: newResult,
        resultManuallySet: isManual,
      });
    },
    [updateMeasure, measureType, index, data]
  );

  /** discussion 필드를 위한 가상 FieldConfig */
  const discussionFieldConfig: FieldConfig = {
    name: "discussion",
    label: "평가",
    type: "text",
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* 헤더 */}
      <div
        className="flex w-full items-center justify-between bg-blue-50 px-4 py-3"
        style={{ minHeight: 44 }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Ruler className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-[14px] font-semibold text-gray-800 truncate">
            {typeName}
            {displayNumber != null && <span className="text-blue-500 ml-1">#{displayNumber}</span>}
          </span>
          <JudgmentBadge result={result} />
        </div>
        <button
          type="button"
          className="flex items-center justify-center gap-1 h-7 px-2 rounded-lg bg-red-500 text-white active:bg-red-600 transition-colors flex-shrink-0"
          onClick={handleDelete}
          aria-label="삭제"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* 측정값 + 평가 + 액션 버튼 */}
      <div className="border-t border-gray-100">
        {/* 측정값 필드 - 각 필드 개별 클릭으로 편집 */}
        <div className="px-4 py-3">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            측정값
          </p>
          <div className="divide-y divide-gray-50">
            {config.fields.map((field) => (
              <ReadOnlyField
                key={field.name}
                field={field}
                value={data[field.name]}
                onClick={() =>
                  setEditingField({
                    field,
                    value: data[field.name],
                  })
                }
              />
            ))}
          </div>
        </div>

        {/* 판정 토글 버튼 */}
        <div className="px-4 py-3 border-t border-gray-100">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            판정
          </p>
          <JudgmentToggleButtons result={result} onToggle={handleJudgmentToggle} />
        </div>

        {/* 계측 사진 */}
        <div className="px-4 py-3 border-t border-gray-100">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            사진
          </p>
          <MeasurePhoto
            checkPhotoUrl={photo?.checkUrl || undefined}
            resultPhotoUrl={photo?.resultUrl || undefined}
            onPhotoCapture={uploadPhoto}
            onPhotoDelete={deletePhoto}
            uploadingSlot={uploadingSlot}
          />
        </div>

        {/* 평가 (discussion) - 인풋박스 디자인 */}
        <div className="px-4 py-3 border-t border-gray-100">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            평가
          </p>
          <button
            type="button"
            onClick={() =>
              setEditingField({
                field: discussionFieldConfig,
                value: discussion ?? "",
                inputType: "textarea",
              })
            }
            className="w-full text-left rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 min-h-[60px] active:bg-gray-100 transition-colors"
          >
            {discussion ? (
              <p className="text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                {discussion}
              </p>
            ) : (
              <p className="text-[14px] text-gray-400">평가를 입력하세요</p>
            )}
          </button>
        </div>
      </div>

      {/* 필드 편집 모달 */}
      {editingField && (
        <FieldEditModal
          open
          onClose={() => setEditingField(null)}
          field={editingField.field}
          value={editingField.value}
          onSave={handleFieldSave}
          isSubmitting={mutationLoading}
          inputType={editingField.inputType}
        />
      )}
    </div>
  );
}
